import _ from 'underscore';

import {
  ActionHelpers,
  standardActionErrorMsg,
  standardActionSuccessMsg
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorItem, MultipleItemResult } from '~commonInfra/contentActions/MultipleItemResult';
import {
  openReactConfirmationDialog,
  ReactConfirmationDialogAreYouSureBody,
  RecycleBinHelpLinkDialog
} from '~commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { L10n } from '~commonInfra/L10n/L10n';
import { Set } from '~commonInfra/utils/Set';
import { Formatters, Messages } from '~src/codegen/Localize';
import { CollectionMapper } from '~src/Collections/mappers/CollectionMapper';
import { CollectionsResourceQuery } from '~src/Collections/ResourceQuery/CollectionsResourceQuery';
import { CollectionErrors } from '~src/Collections/services/CollectionErrors';
import { DeleteCollectionData } from '~src/Collections/space/CollectionModels';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import {
  ConnectedApplicationType,
  IConnectedApplicationInfo
} from '~src/connectedApplications/models/ConnectedApplicationInfo';
import { ConnectedApplications } from '~src/connectedApplications/resources/ConnectedApplicationsResource';
import { ConnectedApplicationErrors } from '~src/connectedApplications/services/ConnectedApplicationErrors';
import { IDataRoleInfo } from '~src/dataRole/models/IDataRoleInfo';
import { DataRoles } from '~src/dataRole/resources/Dataroles';
import { DataRoleErrors } from '~src/dataRole/services/DataRoleErrors';
import { IDataSourceInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { DataSourceErrors } from '~src/datasource/services/DataSourceErrors';
import { IDataAlertBase } from '~src/dda/models/IDataAlertInfo';
import { DataAlerts } from '~src/dda/resources/DataAlerts';
import { deleteDrafts } from '~src/drafts/requests/DraftsRequests';
import { ContentDraftStatus } from '~src/drafts/serverApi/types/ContentDraft';
import { isUnpublishedWorkbookDraft } from '~src/drafts/utils/DraftUtils';
import { DeleteRunFlowTasksAction } from '~src/flow/actions/DeleteRunFlowTasksAction';
import { FlowListItemInfo } from '~src/flow/models/FlowInfo';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { Flows } from '~src/flow/resources/Flows';
import { RunFlowTasks } from '~src/flow/resources/RunFlowTasks';
import { FlowErrors } from '~src/flow/services/FlowErrors';
import { IError } from '~src/httpClient/serverApi/types/IError';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { ILensInfo } from '~src/lens/models/ILensInfo';
import { Lenses } from '~src/lens/resources/Lenses';
import { LensErrors } from '~src/lens/services/LensErrors';
import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';
import { IPredictiveModelInfo } from '~src/predictiveModel/models/IPredictiveModelInfo';
import { PredictiveModels } from '~src/predictiveModel/resources/PredictiveModels';
import { PredictiveModelErrors } from '~src/predictiveModel/services/PredictiveModelErrors';
import { ProjectErrors } from '~src/projects/actions/ProjectErrors';
import { IProjectInfo } from '~src/projects/models/IProjectInfo';
import { Projects } from '~src/projects/resources/Projects';
import { ProjectHooks, projectQueryKeys } from '~src/projects/services/Queries';
import { IPublishedConnectionInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import { PublishedConnections } from '~src/publishedConnection/resources/PublishedConnections';
import { PublishedConnectionErrors } from '~src/publishedConnection/services/PublishedConnectionErrors';
import { FinalDeleteArchivedContent } from '~src/recycleBin/serverApi/requests/FinalDeleteArchivedContentRequest';
import { ArchivedContent } from '~src/recycleBin/serverApi/types/FinalDeleteArchivedContentItemParams';
import { RECYCLE_BIN_CONTENT_TYPE_DICTIONARY } from '~src/recycleBin/space/RecycleBinSpace';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { DeleteSchedulesActionService } from '~src/schedule/actions/DeleteSchedulesAction';
import { SchedulesService } from '~src/schedule/resources/Schedules';
import { ScheduleErrorsService } from '~src/schedule/services/ScheduleErrors';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { getViewInfos } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';
import { isRecycleBinSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { IViewDetailInfo, IViewInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';
import { CustomViews } from '~src/views/ServerApi/requests/CustomViews/CustomViews';
import { ICustomViewInfo } from '~src/views/ServerApi/requests/CustomViews/ICustomViewInfo';
import { CustomViewErrors } from '~src/views/services/customViews/CustomViewErrors';
import { ViewErrors } from '~src/views/services/ViewErrors';
import { IWorkbookInfo, IWorkbookParentInfo } from '~src/workbook/models/IWorkbookInfo';
import { Workbooks } from '~src/workbook/resources/Workbooks';
import { WorkbookErrors } from '~src/workbook/services/WorkbookErrors';

import { DataAlertErrors } from '../../dda/services/DataAlertErrors';

export interface ContentTypeAndID {
  contentType: ExplorableItemType;
  id: string;
}
export interface IDeleteContentsResult extends IResult {
  deletedItems: ContentTypeAndID[];
}

// Determine if any workbooks will be deleted as a result of deleting the selected views
function determineWorkbooksToBeDeleted(views: Set<IViewInfo | IViewDetailInfo>): IWorkbookParentInfo[] {
  const workbooksToDelete: IWorkbookParentInfo[] = [];
  const workbookViewCount: _.Dictionary<number> = {};
  views.each((view: IViewInfo) => {
    let viewCount = workbookViewCount[view.workbook.id] || 0;
    viewCount++;
    if (viewCount === view.workbook.numSheets) {
      workbooksToDelete.push(view.workbook);
    }
    workbookViewCount[view.workbook.id] = viewCount;
  });
  return workbooksToDelete;
}

export function finalDeleteArchivedContents(contents: IContentInfo[]): Promise<IResult> {
  let aggregateResult: IDeleteContentsResult[] = [];
  let outstandingPromises: Promise<IResult>[] = [];

  // Map regular content items to API request Type
  const archivedContents: ArchivedContent[]  = contents.map(content => {
    return <ArchivedContent> {
      contentId: content.id,
      contentType: content.contentType
    };
  });
  const deleteArchivedContents = WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
    RequestHelper.buildRequests(contents, (contents) => new FinalDeleteArchivedContent(archivedContents)),
    ResultHelper.combineResults);

  outstandingPromises.push(deleteArchivedContents);

  deleteArchivedContents.then(
    (result: IResult) => {
      aggregateResult.push({ deletedItems: contents, errors: result.errors });

      if (result.errors && result.errors.length > 0) {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text:
            Messages.deleteArchivedAssetsError({
              ERROR_COUNT_LOC: L10n.localizedCount(result.errors.length),
              CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contents[0].contentType,
                result.errors.length > 1, true))
            })
        });
      } else {
        ToasterService.instance.pop({
          type: ToastType.Success,
          text: (contents.length === 1) ?
            Messages.deleteArchivedAssetSuccess({
              CONTENT_NAME: contents[0].name
          }) :
            Messages.deleteArchivedAssetsSuccess({
              ITEM_COUNT_LOC: L10n.localizedCount(contents.length),
              CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contents[0].contentType, true, true))
            })
        });
      }
    });
  return Promise.all(outstandingPromises).then(() => {
    return combineIDeleteContentsResults(aggregateResult);
  });
}

function confirmDeleteContents(contents: IContentInfo[], spaceInfo?: SpaceInfo): Promise<IDeleteContentsResult|IResult> {
  let projects: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let workbooks: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => {
    return isUnpublishedWorkbookDraft(contentInfo) ? contentInfo.draftLuid : contentInfo.id;
  });
  let dataRoles: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let lenses: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let datasources: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let views: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let flows: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let predictiveModels: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let publishedConnections: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let collections: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let alerts: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let schedules: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let runFlowTasks: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let customViews: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  let connectedApplications: Set<IContentInfo> = new Set<IContentInfo>((contentInfo: IContentInfo) => contentInfo.id);
  contents.forEach((contentItem) => {
    switch (contentItem.contentType) {
      case 'project':
        projects.add(contentItem);
        break;
      case 'workbook':
        workbooks.add(contentItem);
        break;
      case 'dataRole':
        dataRoles.add(contentItem);
        break;
      case 'lens':
        lenses.add(contentItem);
        break;
      case 'predictiveModel':
        predictiveModels.add(contentItem);
        break;
      case 'datasource':
        datasources.add(contentItem);
        break;
      case 'view':
        views.add(contentItem);
        break;
      case 'flow':
        flows.add(contentItem);
        break;
      case 'virtualConnection':
        publishedConnections.add(contentItem);
        break;
      case 'collection':
        collections.add(contentItem);
        break;
      case 'dataalert':
        alerts.add(contentItem);
        break;
      case 'schedule':
        schedules.add(contentItem);
        break;
      case 'runFlowTask':
        runFlowTasks.add(contentItem);
        break;
      case 'customView':
        customViews.add(contentItem);
        break;
      case 'connectedApplication':
        connectedApplications.add(contentItem);
        break;
      default:
        break;
    }
  });

  // Determine if all items selected are supported for recycle bin.
  const isEverySelectedItemRecycleBinSupported = contents.every(
    value => value.contentType in RECYCLE_BIN_CONTENT_TYPE_DICTIONARY);

  // Determine if the selection contains atleast one item supported by recycle bin.
  const isAtleastOneItemRecycleBinSupported = contents.some(
    value => value.contentType in RECYCLE_BIN_CONTENT_TYPE_DICTIONARY);

  let workbooksToBeDeleted: IWorkbookParentInfo[] = [];
  let dialogBody: string = '';
  let determineWorkbooksToBeDeletedPromise: Promise<IViewInfo[]>;
  if (views.size() > 0) {
    determineWorkbooksToBeDeletedPromise = getViewInfos(views.toArray());
  } else {
    determineWorkbooksToBeDeletedPromise = Promise.resolve([] as IViewInfo[]);
  }

  return determineWorkbooksToBeDeletedPromise.then((viewDetailArray: IViewInfo[]) => {
    if (views.size() > 0) {
      workbooksToBeDeleted = determineWorkbooksToBeDeleted(Set.fromIdItemArray(viewDetailArray));
    }
    dialogBody = buildConfirmationDialog(
      contents,
      workbooksToBeDeleted,
      spaceInfo,
      isEverySelectedItemRecycleBinSupported,
      isAtleastOneItemRecycleBinSupported);
    return buildDialogBox(
      dialogBody,
      contents,
      spaceInfo,
      projects,
      workbooks,
      dataRoles,
      lenses,
      predictiveModels,
      datasources,
      views,
      flows,
      publishedConnections,
      collections,
      alerts,
      schedules,
      runFlowTasks,
      customViews,
      connectedApplications,
      isAtleastOneItemRecycleBinSupported);
  });
}

function buildDialogBox(
  dialogBody: string,
  contents: IContentInfo[],
  spaceInfo: SpaceInfo,
  projects: Set<IContentInfo>,
  workbooks: Set<IContentInfo>,
  dataRoles: Set<IContentInfo>,
  lenses: Set<IContentInfo>,
  predictiveModels: Set<IContentInfo>,
  datasources: Set<IContentInfo>,
  views: Set<IContentInfo>,
  flows: Set<IContentInfo>,
  publishedConnections: Set<IContentInfo>,
  collections: Set<IContentInfo>,
  alerts: Set<IContentInfo>,
  schedules: Set<IContentInfo>,
  runFlowTasks: Set<IContentInfo>,
  customViews: Set<IContentInfo>,
  connectedApplications: Set<IContentInfo>,
  isAtleastOneItemRecycleBinSupported: boolean
): Promise<IDeleteContentsResult | IResult> {

  const initialModalInstance = openReactConfirmationDialog<void, IDeleteContentsResult | IResult>({
    title: isRecycleBinSpace(spaceInfo) ? Messages.deletePermanentlyMessage() : Messages.deleteMessage(),
    message: dialogBody,
    bodyComponent: isAtleastOneItemRecycleBinSupported && !isRecycleBinSpace(spaceInfo) && RecycleBinUtils.isRecycleBinEnabled() ? RecycleBinHelpLinkDialog : null,
    paramsForAction: undefined,
    actionName: Messages.confirmDeleteButton({ ITEM_COUNT: contents.length, ITEM_COUNT_LOC: Formatters.formatCount(contents.length) }),
    // If the set of content items included any projects and Recycle Bin is disabled, we
    // defer deletion until a second 'Are you sure' confirmation is given, else proceed after the user clicks 'Delete'
    action: () => {
      // If delete happens from the recycle bin space, call API to
      // permanently delete the item and show corresponding Dialog
      if (isRecycleBinSpace(spaceInfo) && RecycleBinUtils.isRecycleBinEnabled()) {
        return finalDeleteArchivedContents(contents);
      // If there aren't any projects or Recycle Bin is enabled, instantly follow delete content flow
      } else if (projects.size() === 0 || RecycleBinUtils.isRecycleBinEnabled()) {
        return deleteContent(projects, workbooks, dataRoles, lenses, predictiveModels, datasources, views, flows, publishedConnections, collections, alerts, schedules, runFlowTasks, customViews, connectedApplications);
      }
      // Else, pop up a confirmation dialog to make sure the user actually wants to permanently delete the project
      else {
        return null;
      }
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });

  return initialModalInstance.result.then((result) => {
    initialModalInstance.dismiss();
    /*
    Return and move on if there are no projects or
    if Recycle Bin is enabled since no confirmation popup needed
    */
    if (projects.size() === 0 || RecycleBinUtils.isRecycleBinEnabled()) {
      return result;
    }

    const modalInstance = openReactConfirmationDialog<void, IDeleteContentsResult | IResult>({
      bodyComponent: ReactConfirmationDialogAreYouSureBody,
      message: Messages.actionNotUndoable(),
      paramsForAction: undefined,
      actionName: Messages.confirmDeleteProjectsDefinitely(),
      action: () => {
        {
          return deleteContent(projects, workbooks, dataRoles, lenses, predictiveModels, datasources, views, flows, publishedConnections, collections, alerts, schedules, runFlowTasks, customViews, connectedApplications);
        }
      },
      isActionDelete: true,
      centerActionButtons: true,
      dialogWidth: TabDialogWidth.width375px
    });
    return modalInstance.result;
  });
}

// exported for testing
export function deleteContent(
  projects: Set<IContentInfo>,
  workbooks: Set<IContentInfo>,
  dataRoles: Set<IContentInfo>,
  lenses: Set<IContentInfo>,
  predictiveModels: Set<IContentInfo>,
  datasources: Set<IContentInfo>,
  views: Set<IContentInfo>,
  flows: Set<IContentInfo>,
  publishedConnections: Set<IContentInfo>,
  collections: Set<IContentInfo>,
  alerts: Set<IContentInfo>,
  schedules: Set<IContentInfo>,
  runFlowTasks: Set<IContentInfo>,
  customViews: Set<IContentInfo>,
  connectedApplications: Set<IContentInfo>
): Promise<IDeleteContentsResult> {
  let aggregateResult: IDeleteContentsResult[] = [];
  let outstandingPromises: Promise<IResult>[] = [];

  if (projects.size() > 0) {
    const deleteProjects = Projects.del(projects.map(item => item.id));
    outstandingPromises.push(deleteProjects);
    deleteProjects.then(
      (result: IResult) => {
        const deletedProjects: ContentTypeAndID[] = getActuallyDeletedItems(projects, result);
        aggregateResult.push({ deletedItems: deletedProjects, errors: result.errors });
        ProjectHooks.invalidateProjectQueries(projectQueryKeys.all());
        ActionHelpers.displayStandardToastsForResult(
          projects,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteProjectsSuccess),
            error: standardActionErrorMsg(Messages.deleteProjectsError),
          },
          (errorItem: IErrorItem<IProjectInfo>) => ProjectErrors.errorDetailsFor(errorItem));
      });
  }

  if (workbooks.size() > 0) {
    const publishedWorkbooks = workbooks.filter(item => !isUnpublishedWorkbookDraft(item));
    const unpublishedWorkbooks = workbooks.filter(item => isUnpublishedWorkbookDraft(item));

    if (publishedWorkbooks.size() > 0 && unpublishedWorkbooks.size() > 0) {
      const deletePublishedWorkbooks = Workbooks.del(publishedWorkbooks.map(item => item.id));
      outstandingPromises.push(deletePublishedWorkbooks);

      const deleteUnpublishedWorkbooks = deleteDrafts(unpublishedWorkbooks.map(item => item.draftLuid));
      outstandingPromises.push(deleteUnpublishedWorkbooks);

      Promise.all([deletePublishedWorkbooks,deleteUnpublishedWorkbooks]).then((results: IResult[]) => {
        const deletedPublishedWorkbooks: ContentTypeAndID[] = getActuallyDeletedItems(publishedWorkbooks, results[0]);
        const deletedUnpublishedWorkbooks: ContentTypeAndID[] = getActuallyDeletedItems(unpublishedWorkbooks, results[1]);

        aggregateResult.push({ deletedItems: deletedPublishedWorkbooks, errors: results[0].errors });
        aggregateResult.push({ deletedItems: deletedUnpublishedWorkbooks, errors: results[1].errors });

        const publishedItemResult = new MultipleItemResult(publishedWorkbooks, results[0]);
        const unpublishedItemResult = new MultipleItemResult(unpublishedWorkbooks, results[1]);

        if (publishedItemResult.errorCount === publishedWorkbooks.size() && unpublishedItemResult.errorCount === unpublishedWorkbooks.size()) {
          // Both published and unpublished draft failed, show consolidated toast
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.deleteWorkbookDraftAndWorkbook_actionError({
              PUBLISHED_ITEM_COUNT: publishedItemResult.errorCount,
              PUBLISHED_ITEM_COUNT_LOC: Formatters.formatCount(publishedItemResult.errorCount),
              UNPUBLISHED_ITEM_COUNT: unpublishedItemResult.errorCount,
              UNPUBLISHED_ITEM_COUNT_LOC: Formatters.formatCount(unpublishedItemResult.errorCount)
            })
          });
        }
        else if(publishedItemResult.successCount === publishedWorkbooks.size() && unpublishedItemResult.successCount === unpublishedWorkbooks.size()){
          // Both published and unpublished failed, show consolidated toast
          ToasterService.instance.pop({
            type: ToastType.Success,
            text: Messages.deleteWorkbookDraftAndWorkbook_actionSuccess({
              PUBLISHED_ITEM_COUNT: publishedItemResult.successCount,
              PUBLISHED_ITEM_COUNT_LOC: Formatters.formatCount(publishedItemResult.successCount),
              UNPUBLISHED_ITEM_COUNT: unpublishedItemResult.successCount,
              UNPUBLISHED_ITEM_COUNT_LOC: Formatters.formatCount(unpublishedItemResult.successCount)
            })
          });
        }
        else {
          // Either published or unpublished failed, show separate toasts
          ActionHelpers.displayStandardToastsForResult(
            publishedWorkbooks,
            results[0],
            {
              success: standardActionSuccessMsg(Messages.deleteWorkbooksSuccess),
              error: standardActionErrorMsg(Messages.deleteWorkbooksError),
            },
            (errorItem: IErrorItem<IWorkbookInfo>) => WorkbookErrors.errorDetailsFor(errorItem));

          ActionHelpers.displayStandardToastsForResult(
            unpublishedWorkbooks,
            results[1],
            {
              success: standardActionSuccessMsg(Messages.deleteWorkbookDraft_actionSuccess),
              error: standardActionErrorMsg(Messages.deleteWorkbookDraft_actionFailed),
            },
            (errorItem: IErrorItem<IWorkbookInfo>) => WorkbookErrors.errorDetailsFor(errorItem));
        }
      });
    }

    if (publishedWorkbooks.size() > 0 && unpublishedWorkbooks.size() === 0) {
      const deletePublishedWorkbooks = Workbooks.del(publishedWorkbooks.map(item => item.id));
      outstandingPromises.push(deletePublishedWorkbooks);
      deletePublishedWorkbooks.then((result: IResult) => {
        const deletedPublishedWorkbooks: ContentTypeAndID[] = getActuallyDeletedItems(publishedWorkbooks, result);
        aggregateResult.push({ deletedItems: deletedPublishedWorkbooks, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          publishedWorkbooks,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteWorkbooksSuccess),
            error: standardActionErrorMsg(Messages.deleteWorkbooksError),
          },
          (errorItem: IErrorItem<IWorkbookInfo>) => WorkbookErrors.errorDetailsFor(errorItem));
      });
    }

    if (unpublishedWorkbooks.size() > 0 && publishedWorkbooks.size() === 0) {
      const deleteUnpublishedWorkbooks = deleteDrafts(unpublishedWorkbooks.map(item => item.draftLuid));
      outstandingPromises.push(deleteUnpublishedWorkbooks);
      deleteUnpublishedWorkbooks.then((result: IResult) => {
        const deletedUnpublishedWorkbooks: ContentTypeAndID[] = getActuallyDeletedItems(unpublishedWorkbooks, result);
        aggregateResult.push({ deletedItems: deletedUnpublishedWorkbooks, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          unpublishedWorkbooks,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteWorkbookDraft_actionSuccess),
            error: standardActionErrorMsg(Messages.deleteWorkbookDraft_actionFailed),
          },
          (errorItem: IErrorItem<IWorkbookInfo>) => WorkbookErrors.errorDetailsFor(errorItem));
      });
    }
  }

  if (dataRoles.size() > 0) {
    const deleteDataRoles = DataRoles.deleteDataRoles(dataRoles.map(item => item.id));
    outstandingPromises.push(deleteDataRoles);
    deleteDataRoles.then(
      (result: IResult) => {
        const deletedDRs: ContentTypeAndID[] = getActuallyDeletedItems(dataRoles, result);
        aggregateResult.push({ deletedItems: deletedDRs, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          dataRoles,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDataRolesSuccess),
            error: standardActionErrorMsg(Messages.deleteDataRolesError),
          },
          (errorItem: IErrorItem<IDataRoleInfo>) => DataRoleErrors.errorDetailsFor(errorItem));
      });
  }

  if (lenses.size() > 0) {
    const deleteLenses = Lenses.deleteLenses(lenses.map(item => item.id));
    outstandingPromises.push(deleteLenses);
    deleteLenses.then(
      (result: IResult) => {
        const deletedLenses: ContentTypeAndID[] = getActuallyDeletedItems(lenses, result);
        aggregateResult.push({ deletedItems: deletedLenses, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          lenses,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteLensesSuccess),
            error: standardActionErrorMsg(Messages.deleteLensesError),
          },
          (errorItem: IErrorItem<ILensInfo>) => LensErrors.errorDetailsFor(errorItem));
      });
  }

  if (predictiveModels.size() > 0) {
    const deletePredictiveModels = PredictiveModels.deletePredictiveModels(predictiveModels.map(item => item.id));
    outstandingPromises.push(deletePredictiveModels);
    deletePredictiveModels.then(
      (result: IResult) => {
        const deletedPredictiveModels: ContentTypeAndID[] = getActuallyDeletedItems(predictiveModels, result);
        aggregateResult.push({ deletedItems: deletedPredictiveModels, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          predictiveModels,
          result,
          {
            success: standardActionSuccessMsg(Messages.deletePredictiveModelsSuccess),
            error: standardActionErrorMsg(Messages.deletePredictiveModelsError),
          },
          (errorItem: IErrorItem<IPredictiveModelInfo>) => PredictiveModelErrors.errorDetailsFor(errorItem));
      });
  }

  if (datasources.size() > 0) {
    const deleteDatasources = DataSources.del(datasources.map(item => item.id));
    outstandingPromises.push(deleteDatasources);
    deleteDatasources.then(
      (result: IResult) => {
        const deletedDSs: ContentTypeAndID[] = getActuallyDeletedItems(datasources, result);
        aggregateResult.push({ deletedItems: deletedDSs, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDatasourcesSuccess),
            error: standardActionErrorMsg(Messages.deleteDatasourcesError),
          },
          (errorItem: IErrorItem<IDataSourceInfo>) => DataSourceErrors.errorDetailsFor(errorItem));
      });
  }

  if (views.size() > 0) {
    const deleteViews = Views.hide(views.map(item => item.id));
    outstandingPromises.push(deleteViews);
    deleteViews.then(
      (result: IResult) => {
        const deletedViews: ContentTypeAndID[] = getActuallyDeletedItems(views, result);
        aggregateResult.push({ deletedItems: deletedViews, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          views,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteViewsSuccess),
            error: standardActionErrorMsg(Messages.deleteViewsError),
          },
          (errorItem: IErrorItem<IViewInfo>) => ViewErrors.errorDetailsFor(errorItem));
      });
  }

  if (flows.size() > 0) {
    const actualFlows = flows.filter(flow => {
      return flow.draftStatus !== ContentDraftStatus.unpublished;
    });
    if (actualFlows.size() > 0) {
      const deleteFlows = Flows.deleteFlows(actualFlows.map(item => item.id));
      outstandingPromises.push(deleteFlows);
      deleteFlows.then(
        (result: IResult) => {
          const deletedFlows: ContentTypeAndID[] = getActuallyDeletedItems(actualFlows, result);
          aggregateResult.push({ deletedItems: deletedFlows, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            actualFlows,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteFlowsSuccess),
              error: standardActionErrorMsg(Messages.deleteFlowsError),
            },
            (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem));
        });
    }

    const flowDrafts = flows.filter(flow => {
      return flow.draftStatus === ContentDraftStatus.unpublished;
    });
    if (flowDrafts.size() > 0) {
      const deleteFlowDrafts = Flows.deleteFlowDrafts(flowDrafts.map(item => item.id));
      outstandingPromises.push(deleteFlowDrafts);
      deleteFlowDrafts.then(
        (result: IResult) => {
          const deletedFlows: ContentTypeAndID[] = getActuallyDeletedItems(flowDrafts, result);
          aggregateResult.push({ deletedItems: deletedFlows, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            flowDrafts,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteFlowDraft_actionSuccess),
              error: standardActionErrorMsg(Messages.deleteFlowDraft_actionFailed),
            },
            (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem));
        });
    }
  }

  if (publishedConnections.size() > 0) {
    const deletePublishedConnections = PublishedConnections.del(publishedConnections.map(item => item.id));
    outstandingPromises.push(deletePublishedConnections);
    deletePublishedConnections.then(
      (result: IResult) => {
        const deletedPublishedConnections: ContentTypeAndID[] = getActuallyDeletedItems(publishedConnections, result);
        aggregateResult.push({ deletedItems: deletedPublishedConnections, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          publishedConnections,
          result,
          {
            success: standardActionSuccessMsg(Messages.deletePublishedConnectionsSuccess),
            error: standardActionErrorMsg(Messages.deletePublishedConnectionsError),
          },
          (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem));
      });
  }

  if (collections.size() > 0) {
    const deleteCollectionsPromise = (new CollectionsResourceQuery()).delete(collections.map(item => item.luid)).then(
      (result: DeleteCollectionData): IResult => {
        return CollectionMapper.mapDeleteCollectionsDataToIResult(result, collections);
      }
    );
    outstandingPromises.push(deleteCollectionsPromise);
    deleteCollectionsPromise.then(
      (result: IResult) => {
        const deletedCollections: ContentTypeAndID[] = getActuallyDeletedItems(collections, result);
        aggregateResult.push({ deletedItems: deletedCollections, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          collections,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteCollectionsSuccess),
            error: standardActionErrorMsg(Messages.deleteCollectionsError),
          },
          (errorItem: IErrorItem<IContentInfo<'collection'>>) => CollectionErrors.errorDetailsFor(errorItem));
      }).catch((error) => {
        return { errors: [{ code: error.data.errorCode }] } as IResult;
      });
  }

  if (alerts.size() > 0) {
    const deleteAlerts = DataAlerts.del(alerts.map(item => item.id));
    outstandingPromises.push(deleteAlerts);
    deleteAlerts.then(
      (result: IResult) => {
        const deletedAlerts: ContentTypeAndID[] = getActuallyDeletedItems(alerts, result);
        aggregateResult.push({ deletedItems: deletedAlerts, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult<IDataAlertBase>(
          alerts as Set<IDataAlertBase>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteDataAlertsSuccess),
            error: standardActionErrorMsg(Messages.deleteDataAlertsError),
          },
          (errorItem: IErrorItem<IDataAlertBase>) => DataAlertErrors.errorDetailsFor(errorItem)
        );
      });
  }

  if (schedules.size() > 0) {
    const deleteSchedules = SchedulesService
      .del(schedules.map(item => item.id));
    outstandingPromises.push(deleteSchedules);
    deleteSchedules.then(
      (result: IResult) => {
        const deleteSchedules: ContentTypeAndID[] = getActuallyDeletedItems(schedules, result);
        aggregateResult.push({ deletedItems: deleteSchedules, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult<IScheduleInfo>(
          schedules as unknown as Set<IScheduleInfo>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteSchedulesSuccess),
            error: standardActionErrorMsg(Messages.deleteSchedulesError),
          },
          (errorItem: IErrorItem<IScheduleInfo>) => ScheduleErrorsService.errorDetailsFor(errorItem));
      });
  }

  if (runFlowTasks.size() > 0) {
    // RunFlowTasks covers both 'FlowTask' and 'LinkedTask' scenarios
    const flowTasks = runFlowTasks.filter(item => (item as unknown as RunFlowTaskInfo).taskType === 'FlowTask');
    const linkedTasks = runFlowTasks.filter(item => (item as unknown as RunFlowTaskInfo).taskType === 'LinkedTask');
    if (flowTasks.size() > 0) {
      const deleteRunFlowTasks = RunFlowTasks.del(flowTasks.map(item => item.id));
      outstandingPromises.push(deleteRunFlowTasks);
      deleteRunFlowTasks.then(
        (result: IResult) => {
          const deletedRunFlowTasks: ContentTypeAndID[] = getActuallyDeletedItems(flowTasks, result);
          aggregateResult.push({ deletedItems: deletedRunFlowTasks, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult<RunFlowTaskInfo>(
            flowTasks as unknown as Set<RunFlowTaskInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteRunFlowTasksSuccess),
              error: standardActionErrorMsg(Messages.deleteRunFlowTasksError),
            },
            (errorItem: IErrorItem<RunFlowTaskInfo>) => DeleteRunFlowTasksAction.errorDetailFor(errorItem));
        });
    }
    if (linkedTasks.size() > 0) {
      const deleteLinkedTasks = LinkedTasks.deleteLinkedTasks(linkedTasks.map(item => (item as unknown as RunFlowTaskInfo).linkedTaskId));
      outstandingPromises.push(deleteLinkedTasks);
      deleteLinkedTasks.then(
        (result: IResult) => {
          const deletedLinkedTasks: ContentTypeAndID[] = getActuallyDeletedItems(linkedTasks, result);
          aggregateResult.push({ deletedItems: deletedLinkedTasks, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult<RunFlowTaskInfo>(
            linkedTasks as unknown as Set<RunFlowTaskInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.deleteLinkedTasksSuccess),
              error: standardActionErrorMsg(Messages.deleteLinkedTasksError),
            },
            // todo jjia: linked tasks errors
            (errorItem: IErrorItem<RunFlowTaskInfo>) => null);//DeleteRunFlowTasksAction.errorDetailFor(errorItem));
        });
    }
  }

  if (customViews.size() > 0) {
    const deleteCustomViews = CustomViews.del(customViews.map(item => item.id));
    outstandingPromises.push(deleteCustomViews);
    deleteCustomViews.then(
      (result: IResult) => {
        const deletedCustomViews: ContentTypeAndID[] = getActuallyDeletedItems(customViews, result);
        aggregateResult.push({ deletedItems: deletedCustomViews, errors: result.errors });
        ActionHelpers.displayStandardToastsForResult(
          customViews as unknown as Set<ICustomViewInfo>,
          result,
          {
            success: standardActionSuccessMsg(Messages.deleteCustomViewsSuccess),
            error: standardActionErrorMsg(Messages.deleteCustomViewsError),
          },
          (errorItem: IErrorItem<ICustomViewInfo>) => CustomViewErrors.errorDetailsFor(errorItem)
        );
      });
  }

  if (connectedApplications.size() > 0) {
    const directTrustConnectedApps =
      connectedApplications.filter(item => {
        const connectedApplicationType = (item as unknown as IConnectedApplicationInfo).connectedApplicationType;
        return connectedApplicationType === ConnectedApplicationType.DIRECT_TRUST;
      });
    const easConnectedApps =
      connectedApplications.filter(item => (item as unknown as IConnectedApplicationInfo).connectedApplicationType === ConnectedApplicationType.EAS);

    if (directTrustConnectedApps.size() > 0) {
      const deleteConnectedApplications =
        ConnectedApplications.deleteConnectedAppsDirectTrust(directTrustConnectedApps.map(item => item.id));
      outstandingPromises.push(deleteConnectedApplications);
      deleteConnectedApplications.then(
        (result: IResult) => {
          const deletedConnectedApplications: ContentTypeAndID[] = getActuallyDeletedItems(directTrustConnectedApps, result);
          aggregateResult.push({ deletedItems: deletedConnectedApplications, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            connectedApplications as unknown as Set<IConnectedApplicationInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.connectedApplicationsConnectedApplicationDeleted),
              error: standardActionErrorMsg(Messages.connectedApplicationsConnectedApplicationDeleteError),
            },
            (errorItem: IErrorItem<IConnectedApplicationInfo>) => ConnectedApplicationErrors.getErrorDetailsForConnectedApp(errorItem)
          );
        });
    }

    if (easConnectedApps.size() > 0) {
      const deleteConnectedApplications = ConnectedApplications.deleteConnectedAppsEas(easConnectedApps.map(item => item.id));
      outstandingPromises.push(deleteConnectedApplications);
      deleteConnectedApplications.then(
        (result: IResult) => {
          const deletedConnectedApplications: ContentTypeAndID[] = getActuallyDeletedItems(easConnectedApps, result);
          aggregateResult.push({ deletedItems: deletedConnectedApplications, errors: result.errors });
          ActionHelpers.displayStandardToastsForResult(
            connectedApplications as unknown as Set<IConnectedApplicationInfo>,
            result,
            {
              success: standardActionSuccessMsg(Messages.connectedApplicationsConnectedApplicationDeleted),
              error: standardActionErrorMsg(Messages.connectedApplicationsConnectedApplicationDeleteError),
            },
            (errorItem: IErrorItem<IConnectedApplicationInfo>) => ConnectedApplicationErrors.getErrorDetailsForConnectedApp(errorItem)
          );
        });
    }
  }

  return Promise.all(outstandingPromises).then(() => {
    return combineIDeleteContentsResults(aggregateResult);
  });
}

function buildConfirmationDialog(
  contents: IContentInfo[],
  workbooksToBeDeleted: IWorkbookParentInfo[],
  spaceInfo: SpaceInfo,
  isEverySelectedItemRecycleBinSupported: boolean,
  isAtleastOneItemRecycleBinSupported: boolean): string {
  let dialogBody = '';
  if (contents.length === 1) {
    let contentName;
    if (contents[0].contentType === 'runFlowTask') {
      const runFlowTaskContent: RunFlowTaskInfo = contents[0] as unknown as RunFlowTaskInfo;
      if (runFlowTaskContent.taskType === 'LinkedTask') {
        return Messages.linkedTasksDeleteWarning();
      } else {
        return Messages.flowTasksDeleteWarning();
      }
    } else {
      contentName = contents[0].name;
    }

    if (contents[0].contentType === 'schedule') {
      return DeleteSchedulesActionService.deleteSingleScheduleBodyText(contents[0] as unknown as IScheduleInfo);
    }

    // RecycleBin message for a single selected item in other spaces
    if (isEverySelectedItemRecycleBinSupported && !isRecycleBinSpace(spaceInfo) && RecycleBinUtils.isRecycleBinEnabled()) {
      return Messages.recycleBinDeleteActionWarningMessage({
        CONTENT_NAME: L10n.elideIfLong(contentName)
      });
    }

    dialogBody += Messages.confirmDeleteContent({
      CONTENT_NAME: L10n.elideIfLong(contentName),
      CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contents[0].contentType))
    });

    switch (contents[0].contentType) {
      case 'project':
        if (RecycleBinUtils.isRecycleBinEnabled()) {
          dialogBody += ' ' + Messages.deleteProjectWarning();
        }
        break;
      case 'collection':
        dialogBody += Messages.deleteCollectionWarning();
        break;
      case 'view':
        dialogBody += Messages.deleteViewWarning();
        break;
      case 'connectedApplication':
        dialogBody += ' ' + Messages.deleteConnectedApplicationWarning({
          ITEM_COUNT: contents.length
        });
    }
  } else {
    if (contents.length  > 1 && contents[0].contentType === 'connectedApplication') {
      dialogBody += ' ' + Messages.deleteConnectedApplicationWarning({
        ITEM_COUNT: contents.length
      });
    }  else if(isRecycleBinSpace(spaceInfo)) {
      dialogBody += Messages.confirmDeleteContentsWithContentType({
        CONTENT_COUNT_LOC: Formatters.formatCount(contents.length),
        CONTENT_TYPE: L10n.elideIfLong(RecycleBinUtils.getLocalizedContentType(contents[0].contentType, true))
      });
    }
    else {
      dialogBody += Messages.confirmDeleteContents({
        CONTENT_COUNT_LOC: Formatters.formatCount(contents.length)
      });
    }
  }

  if (workbooksToBeDeleted.length > 0) {
    const workbooksWarning = Messages.confirmDeleteViewsWorkbookWarning({
      WORKBOOK_COUNT: workbooksToBeDeleted.length,
      WORKBOOK_COUNT_LOC: Formatters.formatCount(workbooksToBeDeleted.length),
      WORKBOOK_NAME: L10n.elideIfLong(workbooksToBeDeleted[0].name)
    });
    dialogBody = dialogBody + ' ' + workbooksWarning;
  }

  if(!isRecycleBinSpace(spaceInfo)) {
    if(isEverySelectedItemRecycleBinSupported && RecycleBinUtils.isRecycleBinEnabled()) {
      return Messages.recycleBinMultiSelectSupportedItems({
        CONTENT_COUNT_LOC: Formatters.formatCount(contents.length)
      });
    }
    if(isAtleastOneItemRecycleBinSupported && RecycleBinUtils.isRecycleBinEnabled()) {
      return Messages.recycleBinMultiSelectSupportedItems_atleastOneSupportedItem({
        CONTENT_COUNT_LOC: Formatters.formatCount(contents.length)
      });
    }
  }
  if (isRecycleBinSpace(spaceInfo)) {
    dialogBody += ' ' + Messages.deleteForeverWarning();
  }
  return dialogBody;
}


// exported for testing
export function getActuallyDeletedItems(items: Set<IContentInfo<ExplorableItemType>>, result: IResult): ContentTypeAndID[] {
  const deletedItems: ContentTypeAndID[] = [];
  items.each((item: IContentInfo<ExplorableItemType>) => {
    if (result.errors && !_.any(result.errors, (error: IError) => {
      if (item.contentType === 'collection') {
        return error.id === item.luid;
      }
      return isUnpublishedWorkbookDraft(item) ? error.id === item.draftLuid : error.id === item.id;
    })) {
      const id = isUnpublishedWorkbookDraft(item) ? item.draftLuid : item.id;
      deletedItems.push({ id, contentType: item.contentType });
    }
  });
  return deletedItems;
}

function combineIDeleteContentsResults(results: IDeleteContentsResult[]): IDeleteContentsResult {
  return {
    deletedItems: ResultHelper.combine(results, (result) => result.deletedItems),
    errors: ResultHelper.combineErrors(results)
  };
}

export const DeleteContentsAction = {
  confirmDeleteContents,
  determineWorkbooksToBeDeleted
};

export type DeleteContentsAction = typeof DeleteContentsAction;
